import React, { useEffect, useState } from 'react'
import { NumberInput } from '@structura/ui'
import type { AppMaterial } from '../AdminPage'
import { useTranslation } from '@structura/i18n'

interface StructuraBitAdmin {
  slug: string
  title: string
  readingTime: string
  date: string
  thumbnail: string
  category: string
  author: string
  description: string
  order?: number
}

type BitsSortKey = 'order' | 'date' | 'title' | 'category'

// The outcome of one (material, language) list request, keyed so only the list the page currently
// wants is ever shown or saved: an older response — success or failure — is not the current one.
interface LoadedAdminBits {
  key: string
  bits: StructuraBitAdmin[]
  error: string | null
}

export interface StructuraBitsSectionProps {
  appMaterial: AppMaterial
  getSessionHeaders: () => HeadersInit
}

const StructuraBitsSection: React.FC<StructuraBitsSectionProps> = ({ appMaterial, getSessionHeaders }) => {
  const { t, language } = useTranslation()
  const [reloadToken, setReloadToken] = useState<number>(0)
  // The reload generation is part of the key: after a save the previous list is no longer current,
  // so the table is replaced by the loading indicator until the persisted ordering arrives — an
  // edit can never be made on a draft that a pending reload would then overwrite.
  const requestKey: string = `${appMaterial}|${language}|${reloadToken}`
  const [loaded, setLoaded] = useState<LoadedAdminBits | null>(null)
  const [saving, setSaving] = useState<boolean>(false)
  const [bitsSortKey, setBitsSortKey] = useState<BitsSortKey>('order')
  const [bitsSaveMessage, setBitsSaveMessage] = useState<string | null>(null)
  const [bitsSaveError, setBitsSaveError] = useState<string | null>(null)

  useEffect(() => {
    // One live request per key (plus one per save, via reloadToken): a newer request aborts the
    // older one and a response that still arrives after cleanup is ignored, so a slow list for
    // the previous language can never be shown — or saved — under the current one.
    const controller: AbortController = new AbortController()
    let active: boolean = true
    const load = async (): Promise<void> => {
      try {
        // Filter to this app's material so admins see (and reorder) only the
        // bits that actually appear in this app's public bits page. Universal
        // bits (those with no `material:` frontmatter) are included for every
        // app's admin.
        const response = await fetch(`/api/StructuraBits?material=${encodeURIComponent(appMaterial)}&language=${encodeURIComponent(language)}`, {
          headers: getSessionHeaders(),
          signal: controller.signal
        })

        if (!response.ok) {
          throw new Error(t('admin.bits.errLoad'))
        }

        const data: StructuraBitAdmin[] = await response.json()
        if (!active) {
          return
        }
        const normalized: StructuraBitAdmin[] = data.map((bit, index) => ({
          ...bit,
          order: typeof bit.order === 'number' && bit.order > 0 ? bit.order : index + 1
        }))
        setLoaded({ key: requestKey, bits: normalized, error: null })
      } catch (err) {
        if (!active || (err as Error).name === 'AbortError') {
          return
        }
        setLoaded({ key: requestKey, bits: [], error: (err as Error).message || t('admin.bits.errLoad') })
      }
    }
    void load()
    return (): void => {
      active = false
      controller.abort()
    }
  }, [appMaterial, language, getSessionHeaders, t, requestKey])

  const current: LoadedAdminBits | null = loaded !== null && loaded.key === requestKey ? loaded : null
  const bitsLoading: boolean = current === null
  const bitsError: string | null = current === null ? null : current.error
  const bits: StructuraBitAdmin[] = current === null ? [] : current.bits
  const dismissBitsError = (): void => setLoaded(prev => prev !== null && prev.key === requestKey ? { ...prev, error: null } : prev)

  const getSortedBits = (): StructuraBitAdmin[] => {
    const copy: StructuraBitAdmin[] = [...bits]
    copy.sort((a, b) => {
      if (bitsSortKey === 'order') {
        const orderA = typeof a.order === 'number' && a.order > 0 ? a.order : Number.MAX_SAFE_INTEGER
        const orderB = typeof b.order === 'number' && b.order > 0 ? b.order : Number.MAX_SAFE_INTEGER
        if (orderA !== orderB) {
          return orderA - orderB
        }
        return a.title.localeCompare(b.title)
      }
      if (bitsSortKey === 'title') {
        return a.title.localeCompare(b.title)
      }
      if (bitsSortKey === 'category') {
        const byCat = a.category.localeCompare(b.category)
        if (byCat !== 0) return byCat
        return a.title.localeCompare(b.title)
      }
      const aDate = new Date(a.date).getTime()
      const bDate = new Date(b.date).getTime()
      if (!isNaN(aDate) && !isNaN(bDate)) {
        return bDate - aDate
      }
      return a.title.localeCompare(b.title)
    })
    return copy
  }

  const updateBitOrder = (slug: string, newOrder: number): void => {
    // Nothing may change while a save is in flight: the POST carries an earlier snapshot and its
    // reload would silently replace any edit made in the meantime.
    if (saving) {
      return
    }
    const safeOrder: number = Number.isFinite(newOrder) && newOrder > 0 ? Math.floor(newOrder) : 0
    setLoaded(prev => prev !== null && prev.key === requestKey
      ? { ...prev, bits: prev.bits.map(bit => (bit.slug === slug ? { ...bit, order: safeOrder } : bit)) }
      : prev)
  }

  const saveBitsOrdering = async (): Promise<void> => {
    if (saving || current === null) {
      return
    }
    setSaving(true)
    setBitsSaveMessage(null)
    setBitsSaveError(null)
    try {
      const ordered: StructuraBitAdmin[] = getSortedBits()
      const payload = ordered.map((bit, index) => ({
        slug: bit.slug,
        order: index + 1
      }))

      const response = await fetch('/api/StructuraBits/order', {
        method: 'POST',
        headers: {
          ...getSessionHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      })

      if (!response.ok) {
        await response.text()
        throw new Error(t('admin.bits.errSave'))
      }

      // The server reorders this visible subset inside the shared ordering (hidden articles keep
      // their slots), so reload (through the keyed, cancellable effect) to show the persisted result.
      setReloadToken(token => token + 1)
      setBitsSaveMessage(t('admin.bits.saved', { count: payload.length }))
    } catch (err) {
      setBitsSaveError((err as Error).message || t('admin.bits.errSave'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="card">
      <div className="card-header">
        <h5 className="mb-0">
          <i className="bi bi-stars me-2"></i>
          {t('admin.menu.structurabits')}
        </h5>
      </div>
      <div className="card-body">
        <p className="text-muted">
          {t('admin.bits.intro')}
        </p>

        {bitsSaveMessage && (
          <div className="alert alert-success alert-dismissible fade show" role="alert">
            {bitsSaveMessage}
            <button type="button" className="btn-close" onClick={() => setBitsSaveMessage(null)}></button>
          </div>
        )}

        {bitsSaveError && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            {bitsSaveError}
            <button type="button" className="btn-close" onClick={() => setBitsSaveError(null)}></button>
          </div>
        )}

        {bitsLoading && (
          <div className="d-flex align-items-center mb-3">
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            <span>{t('admin.bits.loading')}</span>
          </div>
        )}

        {bitsError && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            {bitsError}
            <button type="button" className="btn-close" onClick={dismissBitsError}></button>
          </div>
        )}

        {!bitsLoading && !bitsError && bits.length === 0 && (
          <div className="alert alert-info mb-0">
            <i className="bi bi-info-circle me-2"></i>
            {t('admin.bits.noBits')}
          </div>
        )}

        {!bitsLoading && !bitsError && bits.length > 0 && (
          <>
            <div className="d-flex align-items-center mb-3">
              <label className="me-2 fw-bold">{t('admin.bits.orderBy')}</label>
              <select
                className="form-select form-select-sm"
                style={{ maxWidth: '240px' }}
                value={bitsSortKey}
                onChange={(event) => setBitsSortKey(event.target.value as BitsSortKey)}
              >
                <option value="order">{t('admin.bits.sortExplicit')}</option>
                <option value="date">{t('admin.bits.sortDate')}</option>
                <option value="title">{t('admin.bits.sortTitle')}</option>
                <option value="category">{t('admin.bits.sortCategory')}</option>
              </select>
              <button
                className="btn btn-sm btn-primary ms-3"
                type="button"
                onClick={saveBitsOrdering}
                disabled={saving}
              >
                <i className="bi bi-save me-1"></i>
                {t('admin.bits.saveOrdering')}
              </button>
            </div>

            <div className="table-responsive">
              <table className="table table-sm table-striped align-middle mb-0">
                <thead>
                  <tr>
                    <th style={{ width: '90px' }}>{t('admin.bits.thOrder')}</th>
                    <th>{t('admin.bits.thTitle')}</th>
                    <th style={{ width: '140px' }}>{t('admin.bits.thDate')}</th>
                    <th style={{ width: '160px' }}>{t('admin.bits.thCategory')}</th>
                    <th style={{ width: '140px' }}>{t('admin.bits.thReadingTime')}</th>
                    <th style={{ width: '180px' }}>{t('admin.bits.thSlug')}</th>
                  </tr>
                </thead>
                <tbody>
                  {getSortedBits().map((bit, index) => (
                    <tr key={bit.slug}>
                      <td>
                        <NumberInput
                          className="form-control form-control-sm"
                          style={{ width: '80px' }}
                          integer
                          disabled={saving}
                          value={typeof bit.order === 'number' && bit.order > 0 ? bit.order : index + 1}
                          onCommit={(order) => updateBitOrder(bit.slug, order)}
                        />
                      </td>
                      <td>{bit.title}</td>
                      <td>{bit.date}</td>
                      <td>{bit.category || <span className="text-muted">—</span>}</td>
                      <td>{bit.readingTime || <span className="text-muted">—</span>}</td>
                      <td>
                        <code>{bit.slug}</code>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default StructuraBitsSection
